import { createFileRoute } from '@tanstack/react-router'

import type { SalesChannelGroup } from '@/api/admin-sales-channels-types'
import { useUpdateSalesChannelGroup } from '@/api/api-mutations'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/settings/sales-channels/groups/$groupUuid/edit')({
	component: SalesChannelGroupEdit
})

function SalesChannelGroupEdit() {
	const { groupUuid } = Route.useParams()

	return (
		<RouteModal
			prev='..'
			asDrawer
			requiresData
			loader={async api => await api.getSalesChannelGroup({ path: { salesChannelGroupUuid: groupUuid } })}
		>
			{data => <SalesChannelGroupEditForm group={data} />}
		</RouteModal>
	)
}

function SalesChannelGroupEditForm({ group }: { group: SalesChannelGroup }) {
	const { L } = useI18n()
	const { groupUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()

	const updateSalesChannelGroup = useUpdateSalesChannelGroup()

	const form = useAppForm({
		defaultValues: { name: group.name },
		onSubmit: async ({ value }) => {
			const result = await updateSalesChannelGroup({
				path: { salesChannelGroupUuid: groupUuid },
				body: { name: value.name }
			})
			if (result.isSuccess) {
				toast.success(L.Settings.SalesChannels.Groups.EditSuccess())
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.SalesChannels.Groups.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.TextField
									autoFocus
									label={L.Fields.Name()}
									placeholder={L.Settings.SalesChannels.Groups.NamePlaceholder()}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
